// MF-67 — Journal Types list: the top of the inheritance chain (Type → Category → Severity →
// Template → Entry). Tenant-named and coloured; ordered by DRAG AND DROP (the order used wherever one is picked).
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { configPath } from '../paths'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Typography
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import RowActions from '../components/RowActions'
import ConfirmDelete from '../components/ConfirmDelete'
import ListSearch from '../components/ListSearch'
import ColorBadge from '../components/ColorBadge'
import { useCategories } from '../components/useLookups'
import DialogConfirm from '../../../dialog/DialogConfirm'
import { journalTableSx, tableCardStyle } from '../components/tableStyles'
import { loadTypes, updateTypesList } from '../../../../store/actions/journalTypesActions'
import { deleteType, updateType } from '../../../../services/journal/types.service'

const COLS = { handle: 56, position: 64, severity: 140, severities: 130, actions: 196 }

const Fact = ({ label, children }) => (
  <div className='flex items-center justify-between' style={{ gap: 12, padding: '10px 0' }}>
    <Typography style={{ fontSize: 13, color: 'rgb(100, 116, 139)' }}>{label}</Typography>
    <div style={{ fontSize: 13, color: 'rgb(15, 23, 42)' }}>{children}</div>
  </div>
)

const TypesList = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { list, loading } = useSelector((state) => state.journalTypes.types)
  const [rows, setRows] = useState([])
  const [search, setSearch] = useState('')
  const [dndReady, setDndReady] = useState(false)
  const [savingOrder, setSavingOrder] = useState(false)
  const [preview, setPreview] = useState(null)
  const [toDelete, setToDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  const reload = () => dispatch(loadTypes({ limit: 200 }))

  useEffect(() => {
    dispatch(loadTypes({ limit: 200 }))
  }, [dispatch])

  useEffect(() => setRows(list), [list])

  // react-beautiful-dnd needs a frame after mount under React 18 StrictMode (same trick as ReviewersPanel).
  useEffect(() => {
    const raf = requestAnimationFrame(() => setDndReady(true))
    return () => {
      cancelAnimationFrame(raf)
      setDndReady(false)
    }
  }, [])

  const query = search.trim().toLowerCase()
  const visible = useMemo(
    () => (query ? rows.filter((r) => r.label?.toLowerCase().includes(query)) : rows),
    [rows, query]
  )
  const canReorder = !query && !savingOrder && rows.length > 1

  const onDragEnd = async ({ source, destination }) => {
    if (!destination || destination.index === source.index) return
    const previous = rows
    const next = [...rows]
    const [moved] = next.splice(source.index, 1)
    next.splice(destination.index, 0, moved)
    const changed = next.map((r, i) => ({ r, i })).filter(({ r, i }) => r.order !== i)
    const ordered = next.map((r, i) => ({ ...r, order: i }))
    setRows(ordered)
    setSavingOrder(true)
    try {
      await Promise.all(changed.map(({ r, i }) => updateType(r._id, { order: i })))
      dispatch(updateTypesList(ordered))
      enqueueSnackbar('Order saved', { variant: 'success' })
    } catch {
      setRows(previous)
      enqueueSnackbar('Could not save the new order', { variant: 'error' })
    } finally {
      setSavingOrder(false)
    }
  }

  const onConfirmDelete = async () => {
    setDeleting(true)
    setDeleteError('')
    try {
      const res = await deleteType(toDelete._id)
      if (res?.error) {
        setDeleteError(res.error.description || 'Could not delete the type')
        return
      }
      setToDelete(null)
      reload()
    } catch (err) {
      setDeleteError(err?.response?.data?.error?.description || 'Could not delete the type')
    } finally {
      setDeleting(false)
    }
  }

  const { list: categories } = useCategories()
  const categoriesByType = useMemo(() => {
    const counts = new Map()
    categories.forEach((c) => counts.set(c.type, (counts.get(c.type) || 0) + 1))
    return counts
  }, [categories])

  const positionOf = (row) => rows.findIndex((r) => r._id === row._id) + 1

  return (
    <>
      <div style={{ padding: '8px 8px 24px' }}>
        <ListSearch value={search} placeholder='Search types…' onChange={setSearch}>
          <Typography style={{ fontSize: 13, color: 'rgb(100, 116, 139)' }}>
            {query
              ? `${visible.length} of ${rows.length} types · clear the search to reorder`
              : `${rows.length} types · drag`}
            {!query ? (
              <FontAwesomeIcon
                icon={['fas', 'grip-dots-vertical']}
                style={{ margin: '0 6px', fontSize: 12 }}
              />
            ) : null}
            {!query ? 'to change the order' : null}
          </Typography>
          <Button
            variant='contained'
            onClick={() => navigate(configPath('types', 'add'))}
            startIcon={<FontAwesomeIcon icon={['fas', 'plus']} style={{ fontSize: 11 }} />}
          >
            New type
          </Button>
        </ListSearch>

        {loading && !rows.length ? (
          <div className='flex justify-center' style={{ padding: '40px 0' }}>
            <CircularProgress />
          </div>
        ) : visible.length === 0 ? (
          <div style={{ ...tableCardStyle, padding: 40 }} className='text-center'>
            <Typography style={{ color: 'rgb(100, 116, 139)' }}>
              {query ? 'No types match your search' : 'No types yet — create the first one with New Type.'}
            </Typography>
          </div>
        ) : (
          <div style={tableCardStyle}>
            <DragDropContext onDragEnd={onDragEnd}>
              <Table sx={journalTableSx} style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: COLS.handle }} />
                  <col style={{ width: COLS.position }} />
                  <col />
                  <col style={{ width: COLS.severity }} />
                  <col style={{ width: COLS.severities }} />
                  <col style={{ width: COLS.actions }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>#</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align='center'>Uses severity</TableCell>
                    <TableCell align='center'>Categories</TableCell>
                    <TableCell align='right'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                {dndReady ? (
                  <Droppable droppableId='journal-types'>
                    {(dropProvided) => (
                      <TableBody ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                        {visible.map((row, index) => (
                          <Draggable
                            key={row._id}
                            draggableId={row._id}
                            index={index}
                            isDragDisabled={!canReorder}
                          >
                            {(provided, snapshot) => (
                              <TableRow
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  ...(snapshot.isDragging
                                    ? {
                                        display: 'table',
                                        tableLayout: 'fixed',
                                        width: '100%',
                                        background: '#fff',
                                        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)'
                                      }
                                    : {})
                                }}
                              >
                                <TableCell style={{ width: COLS.handle }}>
                                  <span
                                    {...provided.dragHandleProps}
                                    aria-label='Drag to reorder'
                                    className='items-center justify-center'
                                    style={{
                                      display: 'inline-flex',
                                      width: 28,
                                      height: 28,
                                      borderRadius: 6,
                                      color: 'rgb(148, 163, 184)',
                                      cursor: canReorder ? 'grab' : 'default',
                                      opacity: canReorder ? 1 : 0.35
                                    }}
                                  >
                                    <FontAwesomeIcon icon={['fas', 'grip-dots-vertical']} />
                                  </span>
                                </TableCell>
                                <TableCell style={{ width: COLS.position, color: 'rgb(100, 116, 139)' }}>
                                  {positionOf(row)}
                                </TableCell>
                                <TableCell>
                                  <div className='flex flex-col items-start' style={{ gap: 4 }}>
                                    <ColorBadge label={row.label} color={row.color} />
                                    {row.description ? (
                                      <span style={{ fontSize: 12, color: 'rgb(100, 116, 139)' }}>
                                        {row.description}
                                      </span>
                                    ) : null}
                                  </div>
                                </TableCell>
                                <TableCell align='center' style={{ width: COLS.severity }}>
                                  {row.usesSeverity ? (
                                    'Yes'
                                  ) : (
                                    <span style={{ color: 'rgb(148, 163, 184)' }}>No</span>
                                  )}
                                </TableCell>
                                <TableCell align='center' style={{ width: COLS.severities }}>
                                  {categoriesByType.get(row.code) || 0}
                                </TableCell>
                                <TableCell align='right' style={{ width: COLS.actions }}>
                                  <RowActions
                                    onPreview={() => setPreview(row)}
                                    onEdit={() => navigate(configPath('types', `edit/${row._id}`))}
                                    onDelete={() => {
                                      setDeleteError('')
                                      setToDelete(row)
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                        {dropProvided.placeholder}
                      </TableBody>
                    )}
                  </Droppable>
                ) : null}
              </Table>
            </DragDropContext>
          </div>
        )}
      </div>

      <DialogConfirm
        id='journal-type-preview'
        open={Boolean(preview)}
        onCancel={() => setPreview(null)}
        showActions={false}
        onCloseHeader
        title='Type preview'
        maxWidth='xs'
      >
        {preview ? (
          <div className='flex flex-col' style={{ gap: 16, paddingTop: 8 }}>
            <div
              className='flex flex-col items-center'
              style={{ gap: 10, padding: '24px 16px', borderRadius: 10, background: 'rgb(248, 250, 252)' }}
            >
              <ColorBadge label={preview.label} color={preview.color} size='lg' />
              {preview.description ? (
                <span style={{ fontSize: 13, color: 'rgb(71, 85, 105)', textAlign: 'center' }}>
                  {preview.description}
                </span>
              ) : null}
            </div>
            <div style={{ borderTop: '1px solid rgb(226, 232, 240)' }}>
              <Fact label='Position'>
                {positionOf(preview)} of {rows.length}
              </Fact>
              <Fact label='Categories using it'>{categoriesByType.get(preview.code) || 0}</Fact>
              <Fact label='Colour'>
                <span
                  className='items-center'
                  style={{ display: 'inline-flex', gap: 8, fontFamily: 'monospace' }}
                >
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 4,
                      backgroundColor: preview.color,
                      display: 'inline-block'
                    }}
                  />
                  {preview.color?.toUpperCase()}
                </span>
              </Fact>
            </div>
          </div>
        ) : null}
      </DialogConfirm>

      <ConfirmDelete
        open={Boolean(toDelete)}
        entity='type'
        name={toDelete?.label}
        loading={deleting}
        error={deleteError}
        onCancel={() => setToDelete(null)}
        onConfirm={onConfirmDelete}
      />
    </>
  )
}

export default TypesList
